'use client';

import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import { LayoutGroup, motion } from 'framer-motion';
import React, { forwardRef, useId } from 'react';
import { TouchTarget } from './button';
import { Link } from './link';

type SidebarProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'nav'>;

type SidebarHeaderProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

type SidebarBodyProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

type SidebarFooterProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

type SidebarSectionProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

type SidebarDividerProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'hr'>;

type SidebarSpacerProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

type SidebarHeadingProps = {
  className?: string;
  children: React.ReactNode;
  'aria-label'?: string;
} & React.ComponentPropsWithoutRef<'h3'>;

type SidebarItemProps = {
  current?: boolean;
  className?: string;
  children: React.ReactNode;
} & (
  | Omit<Headless.ButtonProps, 'as' | 'className'>
  | Omit<Headless.ButtonProps<typeof Link>, 'as' | 'className'>
);

type SidebarLabelProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'span'>;

export function Sidebar({ className, ...props }: SidebarProps) {
  return (
    <nav
      {...props}
      className={clsx(
        className,
        'tw:flex tw:h-full tw:min-h-0 tw:flex-col tw:bg-zinc-100 tw:border-r tw:border-zinc-950/5'
      )}
    />
  );
}

export function SidebarHeader({ className, ...props }: SidebarHeaderProps) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'tw:flex tw:flex-col tw:p-4 tw:dark:border-white/5 [&>[data-slot=section]+[data-slot=section]]:tw:mt-2.5'
      )}
    />
  );
}

export function SidebarBody({ className, ...props }: SidebarBodyProps) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'tw:flex tw:flex-1 tw:flex-col tw:overflow-y-auto tw:p-4 [&>[data-slot=section]+[data-slot=section]]:tw:mt-8'
      )}
    />
  );
}

export function SidebarFooter({ className, ...props }: SidebarFooterProps) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'tw:flex tw:flex-col tw:border-t tw:border-zinc-950/5 tw:p-4 tw:dark:border-white/5 [&>[data-slot=section]+[data-slot=section]]:tw:mt-2.5'
      )}
    />
  );
}

export function SidebarSection({ className, ...props }: SidebarSectionProps) {
  const id = useId();
  return (
    <LayoutGroup id={id}>
      <div
        {...props}
        data-slot="section"
        className={clsx(className, 'tw:flex tw:flex-col tw:gap-1')}
      />
    </LayoutGroup>
  );
}

export function SidebarDivider({ className, ...props }: SidebarDividerProps) {
  return (
    <hr
      {...props}
      className={clsx(
        className,
        'tw:my-4 tw:border-t tw:border-zinc-950/5 tw:lg:-mx-4 tw:dark:border-white/5'
      )}
    />
  );
}

export function SidebarSpacer({ className, ...props }: SidebarSpacerProps) {
  return <div aria-hidden="true" {...props} className={clsx(className, 'tw:mt-8 tw:flex-1')} />;
}

export function SidebarHeading({ className, children, ...props }: SidebarHeadingProps) {
  return (
    <h3
      {...props}
      aria-label={props['aria-label'] || 'Section Heading'}
      className={clsx(
        className,
        // 'tw:mb-1 tw:px-2 tw:!text-xs/6 tw:!font-medium tw:!text-zinc-500 tw:dark:!text-zinc-400'
        'tw:mb-1 tw:px-2 tw:!text-xs/6 tw:!font-medium tw:text-zinc-500 tw:dark:!text-zinc-400'
      )}
    >
      {children}
    </h3>
  );
}

export const SidebarItem = forwardRef(function SidebarItem(
  { current, className, children, ...props }: SidebarItemProps,
  ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>
) {
  const classes = clsx(
    // Base
    'tw:flex tw:w-full tw:items-center tw:gap-3 tw:!rounded-lg tw:px-2 tw:py-2.5 tw:text-left tw:text-base/6 tw:font-medium tw:text-zinc-950 tw:sm:py-2 tw:sm:text-sm/5',
    // Leading icon/icon-only
    'data-[slot=icon]:*:tw:size-6 data-[slot=icon]:*:tw:shrink-0 data-[slot=icon]:*:tw:fill-zinc-500 tw:sm:data-[slot=icon]:*:size-5',
    // Trailing icon (down chevron or similar)
    'data-[slot=icon]:last:*:tw:ml-auto data-[slot=icon]:last:*:tw:size-5 tw:sm:data-[slot=icon]:last:*:size-4',
    // Avatar
    'data-[slot=avatar]:*:tw:-m-0.5 data-[slot=avatar]:*:tw:size-7 data-[slot=avatar]:*:[--ring-opacity:10%] tw:sm:data-[slot=avatar]:*:size-6',
    // Hover
    'tw:data-[hover]:bg-zinc-950/5 data-[slot=icon]:*:tw:data-[hover]:fill-zinc-950',
    // Active
    'tw:data-[active]:bg-zinc-950/5 data-[slot=icon]:*:tw:data-[active]:fill-zinc-950',
    // Current
    'data-[slot=icon]:*:tw:data-[current]:fill-zinc-950',
    // Dark mode
    'tw:dark:text-white tw:dark:data-[slot=icon]:*:fill-zinc-400',
    'tw:dark:data-[hover]:bg-white/5 tw:dark:data-[slot=icon]:*:data-[hover]:fill-white',
    'tw:dark:data-[active]:bg-white/5 tw:dark:data-[slot=icon]:*:data-[active]:fill-white',
    'tw:dark:data-[slot=icon]:*:data-[current]:fill-white'
  );

  return (
    <span className={clsx(className, 'tw:relative')}>
      {current && (
        <motion.span
          layoutId="current-indicator"
          className="tw:absolute tw:inset-y-2 tw:-left-4 tw:w-0.5 tw:rounded-full tw:bg-zinc-950 tw:dark:bg-white"
        />
      )}
      {'href' in props ? (
        <Headless.CloseButton
          as={Link}
          {...props}
          className={classes}
          data-current={current ? 'true' : undefined}
          ref={ref}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.CloseButton>
      ) : (
        <Headless.Button
          {...props}
          className={clsx('tw:cursor-default', classes)}
          data-current={current ? 'true' : undefined}
          ref={ref}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
      )}
    </span>
  );
});

export function SidebarLabel({ className, ...props }: SidebarLabelProps) {
  return <span {...props} className={clsx(className, 'tw:truncate')} />;
}
